import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Dumbbell, Clock, Coins, CheckCircle2, ChevronRight } from "lucide-react";
import { translateLevelName, translateWorldTitle } from "../lib/journeyLabels";

/**
 * Compact Daily Workout card. Condensed for phone: a single-row header with a
 * tight body per state (warming up → progress bar, unlocked → task + Start,
 * completed → done line). Target time now scales with board size (backend).
 */
export default function DailyWorkoutCard({ data }) {
  const { t } = useTranslation();
  if (!data) return null;
  const progress = Math.min(100, Math.round((data.tiles_matched_today / data.target_tiles) * 100));
  const unlocked = data.challenge;
  const completed = unlocked?.status === "completed";
  const bonus = unlocked ? unlocked.base_reward_coins * unlocked.reward_multiplier : 0;
  const localizedLevel = unlocked
    ? (() => {
        const worldTitle = translateWorldTitle({ world_id: unlocked.world_id, title: unlocked.world_title }, t);
        const levelName = translateLevelName(unlocked.level_id, t, unlocked.level_name);
        return worldTitle ? `${worldTitle} · ${levelName}` : levelName;
      })()
    : "";

  return (
    <div
      data-testid="daily-workout-card"
      className="p-3.5 rounded-2xl relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #2C332A 0%, #3E4D40 60%, #5F7161 100%)", color: "#F7F5F0" }}
    >
      <div className="flex items-center gap-2.5 relative">
        <div className="w-9 h-9 rounded-xl bg-white/12 grid place-items-center shrink-0">
          <Dumbbell size={18} strokeWidth={1.7} className="text-[#E8A365]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] uppercase tracking-[0.22em] text-[#E8A365] leading-none">{t("daily_workout.label")}</p>
          <h3 style={{ fontFamily: "Outfit, sans-serif" }} className="text-sm font-semibold leading-tight mt-0.5 truncate">
            {completed
              ? t("daily_workout.complete")
              : unlocked
              ? localizedLevel
              : t("daily_workout.match_n_tiles", { n: data.target_tiles })}
          </h3>
        </div>
        {completed && <CheckCircle2 size={18} className="text-[#8FBF9A] shrink-0" />}
      </div>

      {/* Warming up → thin progress */}
      {!unlocked && (
        <div className="relative mt-2.5">
          <Progress data-testid="daily-workout-progress" value={progress} className="h-1.5 bg-white/20" />
          <p className="text-[11px] text-white/70 mt-1.5">
            {t("daily_workout.match_progress", { matched: data.tiles_matched_today, target: data.target_tiles })}
          </p>
        </div>
      )}

      {/* Unlocked → compact stats + Start */}
      {unlocked && !completed && (
        <div className="relative mt-2.5 flex items-center gap-3">
          <div className="flex items-center gap-3 text-[11px] text-white/80 flex-1 min-w-0">
            <span className="flex items-center gap-1 whitespace-nowrap"><Clock size={12} /> {formatDuration(unlocked.target_time_sec)}</span>
            <span className="flex items-center gap-1 whitespace-nowrap text-[#F0C98B]"><Coins size={12} /> +{bonus}</span>
          </div>
          <Link to={`/play/${unlocked.world_id}/${unlocked.level_id}`} className="shrink-0">
            <Button
              data-testid="daily-workout-start-btn"
              size="sm"
              className="rounded-full h-8 px-4 bg-[#E8A365] hover:bg-[#D9924E] text-[#2C332A] font-semibold text-xs"
            >
              {t("daily_workout.start_btn")} <ChevronRight size={14} strokeWidth={2.4} className="ml-0.5" />
            </Button>
          </Link>
        </div>
      )}

      {completed && (
        <p className="relative text-[11px] text-white/65 mt-1.5">{t("daily_workout.come_back_tomorrow")}</p>
      )}
    </div>
  );
}

function formatDuration(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec}s`;
  return sec === 0 ? `${m}m` : `${m}m ${sec}s`;
}
